/**
 * Centralized User-Facing Messages
 *
 * All user-facing toast messages, error messages, and notifications
 * should be defined here to ensure consistency and enable future i18n.
 *
 * Usage:
 *   import { Messages } from '@/constants/messages'
 *   toast.error(Messages.Assignment.alreadyAssigned('123 Main St'))
 */

// =============================================================================
// ASSIGNMENT MESSAGES
// =============================================================================
export const AssignmentMessages = {
  // Errors
  alreadyAssigned: (propertyDisplay: string) =>
    `User is already assigned to ${propertyDisplay}. Please unassign them first before reassigning to a new property.`,
  tenantAlreadyAssigned: (propertyDisplay: string) =>
    `Tenant is already assigned to ${propertyDisplay}. Please unassign them first before reassigning to a new property.`,
  currentlyAssigned: (propertyDisplay: string) =>
    `Tenant is currently assigned to ${propertyDisplay}. Please unassign them first before reassigning to a new property.`,
  assignmentFailed: 'Failed to assign tenant to property',
  unassignmentFailed: 'Failed to unassign tenant from property',
  reassignmentFailed: 'Failed to reassign tenant',
  propertyNotFound: 'Property not found',
  noActiveAssignment: 'No active assignment found',
  selectUnit: 'Please select a unit for this property',

  // Validation
  onlyTenantsCanBeAssigned: 'Only tenant users can be assigned to properties',
  userNotFound: 'User not found',
  validationFailed: (message: string) => `Validation failed: ${message}`,

  // Success
  assignmentSuccess: 'Tenant assigned successfully',
  unassignmentSuccess: 'Tenant unassigned successfully',
  reassignmentSuccess: 'Tenant reassigned successfully',

  // Unexpected errors
  unexpectedAssignError:
    'An unexpected error occurred while reassigning the tenant.',
  unexpectedUnassignError:
    'An unexpected error occurred while unassigning the tenant.',
}

// =============================================================================
// USER MANAGEMENT MESSAGES
// =============================================================================
export const UserMessages = {
  // Errors
  deleteFailed: (error?: string) =>
    error ? `Failed to delete user: ${error}` : 'Failed to delete user',
  deleteNotSupported: 'Deletion not supported for this user role',
  unexpectedDeleteError: 'An unexpected error occurred while deleting the user',
  saveFailed: 'Failed to save changes',

  // Success
  deleteSuccess: 'User deleted successfully',
  deleting: 'Deleting team member…',
  sectionSaved: (sectionName: string) => `${sectionName} saved`,
  userCreated: (role: string, email: string) =>
    `Created ${role} user: ${email}`,

  // Loading
  loadAdminsFailed: 'Failed to load admin users',
  loadPropertiesFailed: 'Failed to load properties',
}

// =============================================================================
// TENANT MESSAGES
// =============================================================================
export const TenantMessages = {
  deleting: 'Deleting tenant…',
  deleted: 'Tenant deleted',
  // Errors
  deleteFailed: (error?: string) =>
    error ? `Failed to delete tenant: ${error}` : 'Failed to delete tenant',
  unexpectedDeleteError:
    'An unexpected error occurred while deleting the tenant.',
  unassignFailed: (error?: string) =>
    error ? `Failed to unassign tenant: ${error}` : 'Failed to unassign tenant',
  unexpectedUnassignError:
    'An unexpected error occurred while unassigning the tenant.',
  reassignFailed: (error?: string) =>
    error ? `Failed to reassign tenant: ${error}` : 'Failed to reassign tenant',
  purgeFailed: 'Failed to purge tenant from properties',

  // Success
  purgeSuccess: (propertyCount: number) =>
    propertyCount > 0
      ? `Purged from ${propertyCount} property(ies) and cleared assignment`
      : 'Cleared property assignment from tenant record',

  // Info
  purgeNoAssociations: 'No property associations found to purge',
}

// =============================================================================
// INVITATION MESSAGES
// =============================================================================
export const InvitationMessages = {
  // An invitation that can't be opened (accept page, D83)
  closedTitle: 'Invalid invitation',
  closedBefore:
    'This invitation may have already been accepted or has expired. Try signing in to your account or ',
  closedLink: 'contact your admin for support',
  closedAfter: '.',
  closedSignIn: 'Sign in',
  contactTitle: 'Contact your admin',
  contactText:
    "We'll let your admin know your invitation can't be opened. They'll contact you at the email address your invitation was sent to.",
  contactCancel: 'Cancel',
  contactSend: 'Send',
  contactSent: 'Sent. Your admin will be in touch.',
  contactFailed: "Couldn't send. Please try again.",
  // Super admin invitations (PB-72)
  superAdminOnly: 'Only a super admin can invite a super admin.',
  superAdminRoleNote: [
    'Super admins can see and change every account.',
    "They'll set up two-step sign-in the first time they sign in.",
  ],
  // Errors
  createFailed: (error?: string) =>
    error
      ? `Failed to create invitation: ${error}`
      : 'Failed to create invitation',
  updateStatusFailed: (error?: string) =>
    error
      ? `Failed to update request status: ${error}`
      : 'Failed to update request status',
  rejectFailed: (error?: string) =>
    error ? `Failed to reject request: ${error}` : 'Failed to reject request',
  resendInvalidStatus: (status: string) =>
    `Cannot resend: invitation is ${status}`,
  resendFailed: (error: string) => `Failed to resend invitation: ${error}`,
  approveFailed: 'Failed to approve request',
  rejectError: 'Failed to reject request',
  deleteCancelledFailed: 'Failed to delete cancelled invitations',
  clearCancelledFailed: 'Failed to clear cancelled invitations',

  // Success
  cancelled: 'Invitation cancelled',
  sentTo: (email: string) => `Invitation sent to ${email}`,
  resent: 'Invitation email resent!',
  deletedCancelled: (count: number) =>
    `Deleted ${count} cancelled invitation${count !== 1 ? 's' : ''}`,

  // Info
  noCancelledToDelete: 'No cancelled invitations to clear',

  // Warnings
  requestRejected: (email: string) => `Request from ${email} rejected`,

  // Account creation
  welcomeAccountCreated: 'Welcome! Your account has been created.',
  welcome: 'Welcome!',
}

// =============================================================================
// MAINTENANCE & PHOTO MESSAGES
// =============================================================================
export const MaintenanceMessages = {
  // New tenant whose property link hasn't arrived yet (PB-76): the record
  // names a property, onInvitationAccepted is still adding them to it
  settingUpTitle: 'Setting up your account…',
  settingUpBody: 'Your property will appear here in a moment.',
  // On-site visit pending (PB-38): the tenant can't scan until it's closed
  onsiteVisitTitle: 'On-site visit scheduled',
  onsiteVisitBody:
    'Your property admin will check this equipment in person. Scanning is paused until they finish.',
  // Errors
  selectPhotoFirst: 'Please select a photo first',
  cannotUpload: (issue: string) => `Cannot upload: ${issue}`,
  uploadFailed: 'Failed to upload photo. Please try again.',
  loadPhotosFailed: 'Failed to load photos',
  noPhotosToApprove: 'No photos to approve',
  noPhotosToReject: 'No photos to reject',
  approveFailed: 'Failed to approve photos',
  rejectFailed: 'Failed to reject photos',
  provideRejectionReason: 'Please provide rejection reason',
  alertMissingInfo: 'Unable to send alert - missing required information',
  alertFailed: 'Failed to send alert. Please try again.',

  // Success
  uploadSuccess: (filterInfo?: string) =>
    filterInfo
      ? `Photo uploaded successfully - ${filterInfo}`
      : 'Photo uploaded successfully',
  photosApproved: 'Photos approved and maintenance status updated',
  photosApprovedComplete: 'Photos approved and maintenance marked complete',
  alertSent: (name: string) => `Maintenance alert sent to ${name}`,

  // Rejection
  photosRejected: 'Photos rejected. Tenant will be notified to resubmit.',

  // Property Details (D29): recurring scans are reviewed on the Maintenance page
  reviewOnMaintenancePage:
    'Filter changes are reviewed on the Maintenance page.',
  reviewOnMaintenancePageAction: 'Review in Maintenance',
}

// =============================================================================
// AUTHENTICATION MESSAGES
// =============================================================================
export const AuthMessages = {
  // Errors
  sessionExpired: 'Your session has expired. Please sign in again.',
  unauthorized: 'You are not authorized to perform this action.',
  resetPasswordFailed: 'Failed to send reset link. Please try again.',

  // Success
  codeSent: 'Verification code sent to your email.',
  signInSuccess: 'Signed in successfully.',
  signOutSuccess: 'Signed out successfully.',
  resetPasswordSent:
    'If an account exists with that email, a password reset link has been sent.',
}

// =============================================================================
// VALIDATION MESSAGES
// =============================================================================
// Invite-only sign-up (PB-64 / PB-60): someone with no invitation
export const InviteOnlyMessages = {
  // Request access form (PB-87)
  requestTooMany: 'Too many requests from this network. Try again in an hour.',
  requestFailed: 'Failed to submit invitation request. Please try again.',
  noInvitationTitle: (email: string) =>
    email
      ? `There's no invitation for ${email}`
      : "There's no invitation for this email",
  noInvitationBody: 'MNTND is invitation-only.',
  manageProperties: 'Manage properties?',
  requestAccess: 'Request access',
  // A tenant invites their landlord (Q8 tenant part / Q14 B)
  renting:
    'Are you renting? Do you want your landlord or property manager to use MNTND?',
  inviteThem: 'Invite them',
  landlordEmailLabel: "Your landlord's or property manager's email",
  sendInvite: 'Send invitation',
  sending: 'Sending…',
  inviteSent: (email: string) =>
    `Thanks. If ${email} isn't on MNTND yet, we've emailed them about it.`,
  invalidEmail: 'Enter a valid email address.',
  sameEmail: "Enter your landlord's or property manager's email, not your own.",
  tooMany: 'Too many invitations from this network. Try again in an hour.',
  inviteFailed: "We couldn't send it. Try again in a few minutes.",
}

// Two-step authentication (PB-62; user 2026-10-27: the common name)
export const TwoStepMessages = {
  // Admin's phone step after accepting an invitation (PB-88, D129)
  phoneStepTitle: 'Confirm your phone number',
  phoneStepText: "Enter a mobile number. We'll text you a code.",
  phoneStepRates: 'Message and data rates may apply.',
  phoneStepDone: 'Done',
  phoneStepResendPrompt: "Didn't get a code?",
  phoneStepResend: 'Send again',
  phoneStepResent: 'New code sent.',
  phoneStepChangeNumber: 'Use a different number',
  phoneStepSignOut: 'Sign out',
  // Signing in
  title: 'Two-factor authentication (2FA)',
  enterAppCode: 'Enter the 6-digit code from your authenticator app.',
  sendTextPrompt: (label: string) =>
    `We'll send a 6-digit code by text (${label}).`,
  enterTextCode: (label: string) => `Enter the code we sent (${label}).`,
  sendCode: 'Send code',
  sending: 'Sending…',
  codeLabel: 'Code',
  verify: 'Verify',
  checking: 'Checking…',
  switchTo: (label: string) => `Use ${label} instead`,
  cancel: 'Cancel',
  wrongCode: 'That code is wrong or has expired. Try again.',
  codeExpired: 'That code has expired. Send a new one.',
  tooMany: 'Too many tries. Wait a few minutes and try again.',
  failed: (detail?: string) =>
    detail ? `Could not verify: ${detail}` : 'Could not verify',

  // Lost access: recovery through the backup email (PB-81, D98)
  recoveryLink: 'Lost access? Use your backup email',
  recoveryPrompt:
    "We'll send a code to your backup email. This action removes existing two-factor methods. You'll need to set them up again after authentication.",
  recoveryDone:
    'Two-factor authentication has been removed. Sign in, then set up two-factor authentication (2FA) again to continue using 2FA.',
  recoveryNone:
    'No backup email is set up. Contact your admin to reset two-factor authentication.',
  backToSignIn: 'Sign in',

  // Security tab
  sectionTitle: 'Two-factor authentication (2FA)',
  // Backup email (PB-81, D98)
  backupTitle: 'Backup email',
  backupLine:
    "If you lose access to your authenticator app or phone, we'll send a code to this backup email.",
  backupSave: 'Save',
  backupSent: (email: string) =>
    `We sent a code to ${email}. Enter it to confirm this address.`,
  backupSaved: 'Backup email saved',
  backupRemove: 'Remove',
  backupRemoved: 'Removed',
  backupInvalid: 'Enter a valid email address.',
  // The description, split around the two app links (PB-62)
  descriptionStart:
    'After sign-in, enter a code from your phone. Authenticator apps like ',
  googleAuthenticator: 'Google Authenticator',
  descriptionOr: ' or ',
  microsoftAuthenticator: 'Microsoft Authenticator',
  descriptionEnd:
    ' generate a temporary code. SMS text messages send a one-time code via text message.',
  requiredBadge: 'Required',
  recommendedBadge: 'Recommended',
  requiredNote:
    'Setup is required for your account. Add a 2FA method to continue using MNTND.',
  recommendedNote: 'Recommended for admins and managers.',
  none: 'Not set up yet.',
  addApp: 'Authenticator app',
  addText: 'SMS text message',
  remove: 'Remove',
  removed: 'Removed',
  added: (label: string) => `${label} added`,
  appTitle: 'Add an authenticator app',
  appScan:
    'Scan this code with an authenticator app (Google Authenticator, 1Password, Microsoft Authenticator…), then enter the 6-digit code it shows.',
  appKey: 'Can’t scan? Enter this key instead:',
  copyKey: 'Copy key',
  keyCopied: 'Key copied',
  keyCopyFailed: 'Couldn’t copy the key. Select it and copy it instead.',
  textTitle: 'Add text message',
  textPhone: 'Enter a US or Canadian mobile number. We’ll text it a code.',
  passwordTitle: 'Confirm it’s you',
  passwordPrompt:
    'Enter your password to change two-factor authentication (2FA).',
  googlePrompt:
    'Sign in with Google again to change two-factor authentication (2FA).',
  continueWithGoogle: 'Continue with Google',
  wrongPassword: 'Your password is incorrect',
  verifyEmailFirst: 'Confirm your email address before adding 2FA.',
  sendVerification: 'Send confirmation email',
  verificationSent: (email: string) =>
    `We sent a confirmation link to ${email}. Confirm your email then come back to this tab.`,
  verificationTooMany:
    'We already sent a confirmation email a moment ago. Check your inbox, or wait a few minutes and try again.',
  verificationFailed:
    'We couldn’t send the confirmation email. Try again in a few minutes.',
  invalidPhone: 'Enter a valid US or Canadian mobile number',
  regionNotAllowed: 'Texts can only go to US or Canadian numbers',

  // Super admin requirement + reminder
  requiredBanner:
    'Your account requires two-factor authentication (2FA). Add a 2FA method to continue.',
  reminderBanner:
    'Protect your account: turn on two-factor authentication (2FA).',
  reminderAction: 'Set it up',
  reminderDismiss: 'Later',

  // Super admin reset for someone else
  resetTitle: 'Reset two-factor authentication (2FA)',
  resetDescription:
    'For someone who lost their phone: removes all their 2FA methods so they can sign in with their password and set them up again.',
  resetConfirm: 'Reset',
  resetDone: 'Two-factor authentication (2FA) reset',
  resetFailed: (detail?: string) =>
    detail ? `Could not reset: ${detail}` : 'Could not reset',
}

// Sign-in methods: connect Google to an existing account (PB-71)
export const SignInMethodMessages = {
  title: 'Sign-in methods',
  description: 'You can sign in to this account with:',
  password: 'Email and password',
  google: 'Google',
  apple: 'Apple',
  connectGoogle: 'Connect Google',
  connectApple: 'Connect Apple',
  finishGoogleWithCode: 'Enter your 2FA code to finish connecting Google.',
  finishAppleWithCode: 'Enter your 2FA code to finish connecting Apple.',
  connectAppleHint:
    'Connect your Apple account to use it as another way to sign in to this account (optional).',
  appleConnected: 'Apple account connected',
  appleDisconnected: 'Apple account removed',
  appleInUse:
    'That Apple account already has its own MNTND account. Use a different Apple account, or ask MNTND support to merge them.',
  connectGoogleHint:
    'Connect your Google account to use it as another way to sign in to this account (optional).',
  disconnect: 'Remove',
  confirmDisconnect: 'Confirm remove',
  googleConnected: 'Google account connected',
  // A connected Google account with a different email (PB-83, D105, D107 A)
  googleEmailNotice: (google: string, current: string) =>
    `Your Google account uses ${google}. Make it your only way to sign in, instead of ${current} and a password?`,
  googleEmailLine: (current: string) =>
    `You'll sign in with Google from now on. ${current} and your password will stop working.`,
  switchToGoogle: (google: string) => `Use ${google}`,
  keepEmail: (current: string) => `Keep ${current}`,
  later: 'Later',
  googleEmailUsed: (google: string) =>
    `Signed in with Google only, as ${google}`,
  googleDisconnected: 'Google account removed',
  googleInUse:
    'That Google account already has its own MNTND account. Use a different Google account, or ask MNTND support to merge them.',
  alreadyConnected: 'Google is already connected',
  popupClosed: 'The Google window was closed before finishing',
  lastMethod: 'You need at least one other way to sign in first',
  failed: (detail?: string) =>
    detail ? `Could not connect: ${detail}` : 'Could not connect',
  // Sign-in page: Google on an address that already has a password account
  existsWithPassword:
    'This email already has an MNTND account with a password. Sign in with your password, then choose "Connect Google" under Profile → Security.',
}

// Changing your own sign-in email (PB-63)
export const EmailChangeMessages = {
  ownedByGoogle: 'Your email comes from your Google account. Change it there.',
  ownedByApple: 'Your email comes from your Apple account. Change it there.',
  notChangeable: 'Email address cannot be changed here',
  changeHint: 'Used to sign in and for notifications',
  dialogTitle: 'Change email',
  dialogDescription:
    "Enter your new address and your current password. We'll send a link to the new address; your email changes when you click it.",
  sent: (email: string) =>
    `We sent a link to ${email}. Your email changes when you click it.`,
  pending: (email: string) =>
    `Waiting for you to confirm ${email}: click the link we sent there.`,
  sameAddress: 'That is already your email address',
  wrongPassword: 'Your password is incorrect',
  invalidEmail: 'Enter a valid email address',
  inUse: 'That address is already used by another account',
  tooMany: 'Too many tries. Wait a few minutes and try again.',
  failed: (detail?: string) =>
    detail ? `Could not send the link: ${detail}` : 'Could not send the link',
}

export const ValidationMessages = {
  // Generic
  required: (field: string) => `${field} is required`,
  invalid: (field: string) => `Invalid ${field}`,
  minLength: (field: string, min: number) =>
    `${field} must be at least ${min} characters`,
  maxLength: (field: string, max: number) =>
    `${field} must be at most ${max} characters`,

  // Specific
  invalidEmail: 'Invalid email format',
  invalidPhone: 'Invalid phone number format',
  enterAtLeastOneEmail: 'Please enter at least one email address',
  // Emergency contact is all-or-none (PB-D16)
  emergencyContactIncomplete:
    'Emergency contact needs a name, phone and relationship, or leave all three empty',
}

// =============================================================================
// ADMIN / DEV TOOL MESSAGES
// =============================================================================
export const AdminMessages = {
  // Cleanup
  cleanupSuccess: (count: number) => `Cleaned up ${count} seed data records`,
  cleanupWithErrors: (errorCount: number) =>
    `Cleanup completed with ${errorCount} errors`,
  cleanupFailed: 'Cleanup failed',
  previewFound: (count: number) => `Found ${count} documents`,
  previewFailed: (error: string) => `Failed to preview cleanup: ${error}`,
  noDocumentsToDelete: 'No documents to delete. Run preview first.',
  deleteSuccess: (count: number) => `Deleted ${count} documents`,
  deleteFailed: (error: string) => `Failed to cleanup: ${error}`,

  // Data cleanup (Dev Tools, super admin)
  dataCleanupScanFailed: 'Scan failed. See the console for details.',
  dataCleanupDeleted: (count: number) => `Deleted ${count} records`,
  dataCleanupDeleteFailed:
    'Delete failed. Some records may have been deleted; scan again to see what is left.',
  dataCleanupConfirmTitle: (count: number) => `Delete ${count} records?`,
  dataCleanupConfirmDescription:
    'This permanently deletes the records listed below. It cannot be undone.',
  dataCleanupFixed: (count: number) => `Fixed ${count} records`,
  dataCleanupFixConfirmTitle: (count: number) => `Fix ${count} records?`,
  dataCleanupFixConfirmDescription:
    'This changes the records listed below. Nothing is deleted.',

  // Record IDs (super admin)
  recordIdCopied: (label: string) => `${label} copied`,
  recordIdCopyFailed: 'Could not copy to the clipboard',

  // Orphaned units
  fixedOrphanedUnits: (count: number) => `Fixed ${count} orphaned units`,
  noOrphanedUnits: 'No orphaned units found',
  fixOrphanedFailed: 'Failed to fix orphaned units',

  // Corporate account fix
  couldNotGetCurrentUser: 'Could not get current user data',
  noAdminUsersFound: 'No Admin users found in the system',
  fixedUsersHierarchy: (count: number) =>
    `Fixed ${count} users across the hierarchy`,
  allUsersCorrect: 'All users already have correct corporateAccountId',
  fixWithErrors: (errorCount: number) =>
    `Fix completed with ${errorCount} errors`,
  fixCorporateIdFailed: 'Failed to fix corporateAccountId',
  cannotRunAsRole: (role: string) => `Cannot run fix as ${role} user`,

  // Display name sync
  displayNamesDryRun: (count: number) =>
    `Dry run: Would update ${count} tenant display names`,
  displayNamesAlreadySynced: 'All display names already in sync',
  displayNamesUpdated: (count: number) =>
    `Updated ${count} tenant display names`,
  displayNamesSyncFailed: 'Failed to sync display names',

  // Phone number backfill
  phoneBackfillDryRun: (count: number) =>
    `Dry run: Would backfill ${count} phone numbers`,
  phoneBackfillNoneNeeded: 'No users need phone number backfill',
  phoneBackfillSuccess: (count: number) =>
    `Backfilled ${count} phone numbers from Firebase Auth`,
  phoneBackfillNotNeeded: 'No users needed phone number backfill',
  phoneBackfillFailed: 'Failed to backfill phone numbers',

  // Account status backfill
  accountStatusDryRun: (count: number) =>
    `Dry run: Would set accountStatus on ${count} users`,
  accountStatusAllSet: 'All users already have accountStatus set',
  accountStatusSuccess: (count: number) =>
    `Set accountStatus to 'active' on ${count} users`,
  accountStatusFailed: 'Failed to backfill account status',

  // Property cleanup
  propertyCleanupDryRun: (count: number) =>
    `Dry run: Would delete ${count} properties with invalid managers`,
  propertyCleanupNoneFound: 'No properties with invalid managers found',
  propertyCleanupSuccess: (count: number) =>
    `Deleted ${count} properties with invalid managers`,
  propertyCleanupNotNeeded: 'No properties needed cleanup',
  propertyCleanupFailed: 'Failed to cleanup properties',

  // User deletion
  userDeleted: (email: string) => `Deleted ${email}`,
  userDeletedWithErrors: (errorCount: number) =>
    `Deleted with ${errorCount} errors`,
  userDeleteFailed: 'Failed to delete user',

  // Coming soon
  featureComingSoon: (feature: string) => `${feature} feature coming soon`,
}

// =============================================================================
// CROSS-ACCOUNT TRANSFER MESSAGES
// =============================================================================
export const TransferMessages = {
  // Errors
  unauthorized: 'Unauthorized: Only Super Admin can view transfer history',
  resourceNotFound: 'Target resource not found',
  userNotFound: 'Requesting user not found',
  accessDenied:
    'Access denied: Resource belongs to different corporate account',
  validationFailed: 'Validation failed',
}

// =============================================================================
// PROPERTY MESSAGES
// =============================================================================
export const PropertyMessages = {
  // Errors
  updateFailed: (error?: string) =>
    error ? `Failed to update property: ${error}` : 'Failed to update property',
  loadFailed: 'Failed to load property',
  notFound: 'Property not found',

  // Success
  updateSuccess: 'Property updated successfully',
}

// =============================================================================
// EQUIPMENT MESSAGES
// =============================================================================
export const EquipmentMessages = {
  // Errors
  addFailed: (error?: string) =>
    error ? `Failed to add equipment: ${error}` : 'Failed to add equipment',
  updateFailed: (error?: string) =>
    error
      ? `Failed to update equipment: ${error}`
      : 'Failed to update equipment',
  deleteFailed: (error?: string) =>
    error
      ? `Failed to delete equipment: ${error}`
      : 'Failed to delete equipment',
  notFound: 'Equipment not found',

  // Success
  addSuccess: 'Equipment added successfully',
  updateSuccess: 'Equipment updated successfully',
  deleteSuccess: 'Equipment deleted successfully',

  // Info
  pendingScan: 'Waiting for tenant to scan equipment details',
  noEquipment: 'No equipment configured',
}

// =============================================================================
// WIZARD MESSAGES
// =============================================================================
export const WizardMessages = {
  // Airflow confirmation
  airflowDetected: (direction: string) =>
    `Airflow arrow pointing ${direction}.`,
  airflowNotDetected: 'We could not detect an airflow arrow in your photo.',
  airflowConfirmQuestion: 'Is this the correct airflow direction?',
  airflowSelectPrompt: 'Please select the airflow direction for this filter.',
  airflowAdminNotice:
    'Your response will be sent to your property admin for confirmation.',
  airflowIntoUnit: 'Into the unit',
  airflowOutOfUnit: 'Out of the unit',
  airflowUnknown: "I don't know",
  airflowConfirmed: 'Airflow direction confirmed',
  airflowPendingReview: 'Pending admin confirmation',

  // Filter validation
  filterClean: 'New filter verified!',
  filterUsedWarning: 'This filter appears to have been used.',
  filterUseNew: 'Please use a NEW filter.',
  filterProceedAnyway: 'Continue anyway',
  filterFlaggedNotice: 'Proceeding will flag this submission for admin review.',
  filterRetake: 'Take new photo',

  // Filter condition (setup wizard)
  filterConditionClean: 'Filter appears clean',
  filterConditionDirty:
    'Your filter appears dirty and should be replaced before continuing.',
  filterConditionDirtyRetakePrompt: 'Photograph the new filter',
  filterConditionReplacedNotice:
    'Filter replaced during setup. Maintenance schedule has been set.',
  incrementalSaveFailed:
    'Could not save progress. Your data will be saved when you complete setup.',
  photoUploadFailed:
    'Photo upload failed. Check your connection and tap Continue to try again.',

  // Photo instructions
  pullFilterHalfway: 'Pull the filter out so 1/3 is exposed',
  pullFilterHalfwayMaintenance:
    'Insert the new filter so 1/3 is exposed (2/3 in the slot)',
  photographNewFilter: 'Photograph the NEW filter being installed',
  photographDataPlate: "Photograph the manufacturer's data plate",
  photographFilter: 'Photograph the filter label showing size and brand',
  airflowReminder: (direction: string) => `Arrow should point ${direction}`,

  // Data plate encouragement
  dataPlateWhyHelps:
    'Making service calls easier for troubleshooting and parts.',
  dataPlateWhereToFind: 'Where to find data plate:',
  dataPlateLocations: [
    'Side of furnace cabinet',
    'Inside blower compartment door',
    'Near access panel on air handler',
  ],
  dataPlateWhatIsIt: 'What is a data plate:',
  dataPlateDescription:
    'A data plate, also known as a nameplate or rating plate, is a permanent identification label found on equipment like furnaces and air conditioners. It serves as the "birth certificate" for the unit, providing technicians and homeowners with essential technical specifications and safety data.',

  // Filter step instructions
  filterSummary:
    "We'll identify your filter size, airflow direction, and set up replacement reminders.",
  filterStep1:
    'Pull the filter out so about 1/3 is exposed — 2/3 should still be inside the slot.',
  filterStep1Maintenance:
    'Slide the new filter into the slot about 2/3 of the way — leave 1/3 exposed for the photo, then push it fully in after.',
  filterStep1DateMark:
    "Write today's date MM/DD/YYYY on the front face of the filter.",
  filterStep2:
    'Angle your camera so the equipment slot edge is visible wrapping over the exposed end of the filter. Hold 6–12 inches (15–30cm) away — the filter should dominate the frame.',
  filterConfirm:
    'The airflow arrow and filter size should both be visible in your photo.',
  filterWhyHelps:
    'Identifying your filter helps us track replacement schedules and order the right filter size.',
  filterHowToPhoto: 'How to photograph:',
  filterPhotoSteps: [
    'Position the filter so 1/3 is exposed and 2/3 is in the slot',
    'Angle the camera so the slot edge wraps over the exposed end of the filter',
    'Photograph the filter showing the filter size, e.g. 25x16x1',
    'Include the airflow arrow if visible',
  ],
  filterAirflowTip:
    "It's important you install the filter in the correct direction. Note the arrow printed on the filter frame, this shows airflow direction.",
  filterConditionTip:
    'If the filter is visibly dirty (grey in color), you will be prompted to replace it before continuing.',
  notADataPlate:
    'This does not appear to be a data plate. Look for a metal or printed label on your HVAC unit that lists technical specifications.',
  skipDataPlateTitle: 'Skip data plate?',
  skipDataPlateDescription:
    'Are you sure you want to skip adding a data plate? Adding a data plate can help technicians service your equipment.',
  dataPlateRequired:
    'Please photograph the data plate to continue, or select "Skip for now" to proceed without it.',

  // Completion
  setupComplete: 'Equipment setup complete!',
  maintenanceComplete: 'Filter replacement complete!',
  nextMaintenance: (date: string) => `Next maintenance scheduled: ${date}`,

  // Scanner state
  scannerLoading: 'Preparing photo scanner...',
  scanning: 'Scanning...',

  // Analysis
  analyzingPhoto: 'Analyzing photo...',
  analysisComplete: 'Analysis complete',
  analysisFailed: 'Photo analysis failed. You can enter details manually.',
  analysisServiceError:
    "We're sorry we couldn't process the image, please try again.",
  lowConfidence:
    'We could not read all details clearly. Please verify the information below.',

  // Soft intercept (v2.0)
  softInterceptTitle: 'Are you sure?',
  softInterceptBody:
    'Based on our analysis, the filter appears to be installed correctly — the arrow is pointing toward the blower. Changing the direction may reduce efficiency or damage your HVAC system. Are you sure you want to change it?',
  softInterceptKeep: 'Keep current direction',
  softInterceptPersist: 'I still want to change it',

  // Admin decline (v2.0)
  adminDecline: 'Decline',
  adminDeclineTitle: 'Request a scan correction',
  adminDeclineDescription:
    'Explain what needs to change. This note will be shown to the tenant.',
  adminDeclinePlaceholder:
    'e.g., "The blower is visible above the filter. The arrow should point upward toward it."',
  adminDeclineSubmit: 'Decline with note',
  adminDeclineSuccess: 'Decline submitted. Tenant will be asked to re-scan.',
  adminDeclineNoteRequired: 'Please provide a note explaining the issue.',

  // Re-photograph flow
  airflowRetakePrompt:
    'Please re-photograph the filter showing the correct airflow arrow direction.',
  airflowCorrectionNote: 'Direction was corrected during setup',

  // Admin scan review (renamed from "Airflow review" in R-2, D-N-048).
  // The admin's review covers airflow direction AND any Gemini installation
  // flags the tenant submitted past — name generalized accordingly.
  equipmentScanReviewNeeded: 'Review Equipment Scan',
  equipmentScanReviewDescription: (property: string, tenant: string) =>
    `${tenant} at ${property} submitted an equipment scan and needs your review.`,
  adminConfirm: 'Confirm',
  adminCorrect: 'Request correction',
  adminUnclear: 'On-site Check',
  reviewConfirmed: 'Equipment scan confirmed',
  reviewCorrected: 'Correction requested. Tenant will be asked to re-scan.',
  reviewFlagged: 'Flagged for on-site verification',
  reviewSuccess: 'Equipment scan review saved',
  reviewFailed: (error: string) =>
    `Failed to save equipment scan review: ${error}`,
}

// =============================================================================
// EQUIPMENT DETAIL MESSAGES
// =============================================================================
export const EquipmentDetailMessages = {
  setupIncomplete: 'Equipment setup has not been completed',
  continueSetup: 'Continue Setup',
  noDataPlatePhoto: 'No data plate photo captured',
  noFilterInfo: 'Filter information not yet captured',
  noMaintenanceSchedule: 'No maintenance schedule set',
  noAirflowInfo: 'Airflow direction not yet determined',
  equipmentNotFound: 'Equipment not found',
  propertyNotFound: 'Property not found',
}

// =============================================================================
// GENERIC MESSAGES
// =============================================================================
export const GenericMessages = {
  // Errors
  unexpectedError: 'An unexpected error occurred. Please try again.',
  networkError: 'Network error. Please check your connection.',
  notFound: 'Resource not found.',
  permissionDenied: 'You do not have permission to perform this action.',

  // Success
  saved: 'Changes saved successfully.',
  deleted: 'Deleted successfully.',
  updated: 'Updated successfully.',
  created: 'Created successfully.',

  // Loading
  loading: 'Loading...',
  saving: 'Saving...',
  deleting: 'Deleting...',
}

// =============================================================================
// COMBINED EXPORT
// =============================================================================
export const Messages = {
  Assignment: AssignmentMessages,
  User: UserMessages,
  Tenant: TenantMessages,
  Invitation: InvitationMessages,
  Maintenance: MaintenanceMessages,
  Auth: AuthMessages,
  Validation: ValidationMessages,
  Admin: AdminMessages,
  Transfer: TransferMessages,
  Property: PropertyMessages,
  Equipment: EquipmentMessages,
  Wizard: WizardMessages,
  Generic: GenericMessages,
}

export default Messages
